// bluaIQ — Project context header (shared across all /projects/:projectId/* modules)
//
// PR-NEW: a single, narrow header rendered above every project module so the
// user always knows which project they are inside, whether it is demo or
// real, and which module they are looking at. Lives in the project shell
// (ProjectLayout in App.tsx), not inside each module — module internals
// are not touched.
//
// Copy contract (verbatim from the task spec):
//   ES real:  Proyecto · <name>
//   EN real:  Project · <name>
//   ES demo:  Proyecto demo · <name>
//   EN demo:  Demo Project · <name>
//   Below the line: the localized current-module label.
//
// Name fallback: if `projectName` is empty/null we render `projectId`
// instead. We never invent a name and never hardcode a real client's
// project name in this file.
//
// Demo path: callers pass `isDemo = isDemoProject(projectId)`. The
// header itself contains no business logic and fires no network calls
// — it is a presentational component only.

import { Folder, Eye } from 'lucide-react'

interface ProjectContextHeaderProps {
  /** Whether the active project is a demo project (callers compute via isDemoProject). */
  isDemo: boolean
  /** Active language: 'es' or anything else (treated as English). */
  isEs: boolean
  /** Real project name from the API, demo project name from demoData, or null. */
  projectName: string | null
  /** Active route projectId — fallback display when projectName is unavailable. */
  projectId: string | undefined
  /** Localized current-module label (e.g. "Recibos" / "Receipts"). May be null on unknown route. */
  moduleLabel: string | null
}

export default function ProjectContextHeader({
  isDemo,
  isEs,
  projectName,
  projectId,
  moduleLabel,
}: ProjectContextHeaderProps) {
  // Display name: prefer the API/demo-data name; otherwise show the
  // route projectId so the user still has SOME identifier on screen.
  // No fake/invented name and no hardcoded real-client name.
  const display = (projectName && projectName.trim().length > 0)
    ? projectName
    : (projectId || (isEs ? 'Proyecto sin nombre' : 'Untitled project'))

  // Bilingual project-line prefix.
  const prefix = isDemo
    ? (isEs ? 'Proyecto demo' : 'Demo Project')
    : (isEs ? 'Proyecto' : 'Project')

  return (
    <div
      data-testid="project-context-header"
      className="px-5 pt-4 pb-2 border-b border-[#ECEAE4] bg-[#FAFAF8]/60"
    >
      <div className="flex items-center gap-2 flex-wrap">
        {isDemo ? (
          <Eye size={12} className="text-[#9E9890]" aria-hidden="true" />
        ) : (
          <Folder size={12} className="text-[#9E9890]" aria-hidden="true" />
        )}
        <span className="text-[10px] font-[800] uppercase tracking-[0.6px] text-[#9E9890]">
          {prefix}
        </span>
        <span className="text-[10px] font-[700] text-[#CECAC4]" aria-hidden="true">·</span>
        <span
          className="text-[12px] font-[800] text-[#1C1A17] truncate max-w-[60ch]"
          data-testid="project-context-header-name"
        >
          {display}
        </span>
        {isDemo && (
          <span
            className="text-[9px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#1C1A17] border border-[#ECEAE4]"
            data-testid="project-context-header-demo-chip"
          >
            {isEs ? 'Solo lectura' : 'Read-only'}
          </span>
        )}
      </div>
      {moduleLabel && (
        <div
          className="text-[11px] font-[700] text-[#6E6860] mt-0.5"
          data-testid="project-context-header-module"
        >
          {moduleLabel}
        </div>
      )}
    </div>
  )
}
